<template>
  <div
    v-if="visible"
    class="fixed inset-0 bg-neutral-100/15 backdrop-blur-[2px] flex justify-center items-center z-[100]"
    @click="handleCancel">
    <div class="core-modal-surface bg-white p-6 w-[480px] border border-neutral-300" @click.stop>
      <div class="flex items-center justify-between border-b border-neutral-300 pb-4 mb-4">
        <h2
          v-tooltip="{ text: titleTooltip, theme: titleTooltipTheme }"
          class="text-xl font-semibold text-neutral-900 cursor-help data-v-tooltip">
          {{ $t('project.setup.sections.complexity.popup.title', { category: categoryLabel }) }}
        </h2>
        <div class="cursor-pointer" @click="handleCancel">
          <IconX class="h-5 w-5" stroke="1.8" />
        </div>
      </div>

      <!-- Justification field -->
      <div class="mb-6">
        <label class="core-form-label">{{ wt('complexity', 'popup', 'justificationLabel') }}</label>
        <textarea
          v-model="localJustification"
          rows="6"
          :placeholder="wt('complexity', 'popup', 'justificationPlaceholder')"
          class="w-full p-2 border border-neutral-300 rounded-sm bg-neutral-25 focus:outline-none resize-none"></textarea>
        <p
          v-if="localJustification.length > 0 && localJustification.length < minChars"
          class="text-xs text-red-500 mt-1">
          {{ $t('project.setup.sections.complexity.popup.min_chars', { count: minChars }) }}
        </p>
      </div>

      <div class="flex flex-col gap-2">
        <button
          :disabled="!canSave"
          @click="handleSave"
          class="w-full h-11 core-action-button core-action-button--primary">
          {{ wt('complexity', 'popup', 'save') }}
        </button>
        <div class="flex gap-2">
          <button
            v-if="hasExistingData"
            @click="handleDelete"
            class="flex-1 h-11 core-action-button core-action-button--supporting">
            {{ wt('complexity', 'popup', 'delete') }}
          </button>
          <button
            @click="handleCancel"
            :class="hasExistingData ? 'flex-1' : 'w-full'"
            class="h-11 core-action-button core-action-button--supporting">
            {{ wt('complexity', 'popup', 'cancel') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, toRef, watch } from 'vue'
import { IconX } from '@tabler/icons-vue'
import { useWidgetText } from '@/helpers/useWidgetText'

export type ComplexityCategory = 'creative' | 'technical' | 'spatial' | 'organizational'

interface Props {
  visible: boolean
  category: ComplexityCategory
  categoryLabel: string
  justificationValue: string | null
  role?: string
}

const props = defineProps<Props>()

const { wt } = useWidgetText('widget-texts-project-setup', toRef(props, 'role'))

// Tooltip theme matching the slider title tooltips
const titleTooltipTheme = { placement: 'top', width: '18rem', padding: '0.6em 1em' } as const

const titleTooltip = computed(() => wt('complexity', 'dimensions', props.category, 'tooltip'))

const emit = defineEmits<{
  save: [justification: string]
  delete: []
  close: []
}>()

const minChars = 10

const localJustification = ref('')

// Sync local value with prop when modal opens
watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      localJustification.value = props.justificationValue || ''
    }
  },
  { immediate: true }
)

const hasExistingData = computed(() => {
  return !!props.justificationValue
})

const canSave = computed(() => {
  return localJustification.value.length >= minChars
})

function handleSave() {
  if (canSave.value) {
    emit('save', localJustification.value)
  }
}

function handleDelete() {
  emit('delete')
}

function handleCancel() {
  emit('close')
}
</script>

<style lang="postcss" scoped>
.core-modal-surface {
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.12),
    0 8px 16px -12px rgba(0, 0, 0, 0.2);
}
</style>
